import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { NeptuneApi } from '../genericApi/neptune.api';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';

import { ViMonitorApi } from './models/vi-monitor.api.model';
import { ViMonitorListItemApi } from './models/vi-monitor-list-item.api.model';
import { MultiBaseAbstractApi } from '../genericApi/multi-base-abstract.api';
import { ViGenerateQuestionsRequestApi, ViGenerateQuestionsResponseApi } from './models/vi-generate-questions.api.model';
import { ViAnalysisRequestApi } from './models/vi-analysis-request.api.model';
import { ViAnswerRequestApi } from './models/vi-answer-request.api.model';
import { ViAnalysisResponseApi } from './models/vi-analysis-response.api.model';
import { ViAnswerApi } from './models/vi-answer.api.model';
import { DisplayGroupPatchModel } from '../displayGroups/models/display-group-patch.api.model';
import { ViRunRequestApi } from './models/vi-run-request.api.model';
import { ViRunResponseApi } from './models/vi-run-response.api.model';

/**
 * API service for the Visibility Intelligence feature.
 * Endpoints: /agents/visibility-intelligence/*
 *
 * This is a sub-resource of the `/agents` API. Split from the main
 * agents API service (`agents/agents.api.ts`) to isolate feature-specific definitions.
 */
@Injectable()
export class ViApiService extends MultiBaseAbstractApi<ViMonitorApi, ViMonitorListItemApi, number> {

    constructor(
        private neptuneApi: NeptuneApi
    ) {
        super();
    }


    getList(): Observable<ViMonitorListItemApi[]> {
        const slug: string = '/agents/visibility-intelligence/monitor/list';

        return this.neptuneApi.api<ViMonitorListItemApi[]>(slug, null, HttpVerbsEnum.GET);
    }

    getFullList(): Observable<ViMonitorApi[]> {
        const slug: string = '/agents/visibility-intelligence/monitor';

        return this.neptuneApi.api<ViMonitorApi[]>(slug, null, HttpVerbsEnum.GET);
    }

    getById(id: number): Observable<ViMonitorApi> {
        const slug: string = `/agents/visibility-intelligence/monitor/${id}`;

        return this.neptuneApi.api<ViMonitorApi>(slug, null, HttpVerbsEnum.GET);
    }

    create(resource: ViMonitorApi): Observable<number> {
        const slug: string = '/agents/visibility-intelligence/monitor';

        return this.neptuneApi.api<ViMonitorApi>(slug, resource, HttpVerbsEnum.POST).pipe(
            map(apiModel => +apiModel.id)
        );
    }

    update(resource: ViMonitorApi): Observable<number> {
        const slug: string = `/agents/visibility-intelligence/monitor/${+resource.id}`;

        return this.neptuneApi.api<ViMonitorApi>(slug, resource, HttpVerbsEnum.PATCH).pipe(
            map(apiModel => +apiModel.id)
        );
    }

    updateFull(resource: ViMonitorApi): Observable<ViMonitorApi> {
        const slug: string = `/agents/visibility-intelligence/monitor/${+resource.id}`;

        return this.neptuneApi.api<ViMonitorApi>(slug, resource, HttpVerbsEnum.PATCH);
    }

    deleteById(id: number): Observable<number> {
        const slug: string = `/agents/visibility-intelligence/monitor/${id}`;

        return this.neptuneApi.api<void>(slug, null, HttpVerbsEnum.DELETE).pipe(
            map(() => id)
        );
    }

    /**
     * Adds and/or removes subscriptions to/from provided group
     * @param addIds Subscriptions to add to group
     * @param removeIds Subscriptions to remove from group
     * @param groupId
     */
    updateDisplayGroup(addIds: number[], removeIds: number[], groupId: number): Observable<void> {
        const slug: string = `/agents/visibility-intelligence/monitor/displaygroups/${groupId}`;

        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };

        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
     * Removes the group ID from provided monitors via removeIds
     * @param removeIds monitorIds to remove
     */
    removeDisplayGroup(removeIds: number[]): Observable<void> {
        const slug: string = '/agents/visibility-intelligence/monitor/displaygroups';

        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: [],
            removeResourceIds: removeIds
        };

        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    updateFavorites(addIds: number[], removeIds: number[]): Observable<void> {
        const slug: string = '/agents/visibility-intelligence/monitor/displaygroups/favourites';

        const patchModel: DisplayGroupPatchModel = {
            addResourceIds: addIds,
            removeResourceIds: removeIds
        };

        return this.neptuneApi.api<void>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    activateMonitor(id: number): Observable<ViMonitorApi> {
        const slug: string = `/agents/visibility-intelligence/monitor/${id}/activate`;

        return this.neptuneApi.api<ViMonitorApi>(slug, null, HttpVerbsEnum.POST);
    }

    pauseMonitor(id: number): Observable<ViMonitorApi> {
        const slug: string = `/agents/visibility-intelligence/monitor/${id}/pause`;

        return this.neptuneApi.api<ViMonitorApi>(slug, null, HttpVerbsEnum.POST);
    }


    generateQuestions(request: ViGenerateQuestionsRequestApi): Observable<ViGenerateQuestionsResponseApi> {
        const slug: string = '/agents/visibility-intelligence/generate-questions';

        return this.neptuneApi.api<ViGenerateQuestionsResponseApi>(slug, request, HttpVerbsEnum.POST);
    }


    getViAnswer(request: ViAnswerRequestApi): Observable<ViAnswerApi> {
        const slug: string = '/agents/visibility-intelligence/answer';

        return this.neptuneApi.api<ViAnswerApi>(slug, request, HttpVerbsEnum.POST);
    }

    getViAnalysis(request: ViAnalysisRequestApi): Observable<ViAnalysisResponseApi> {
        const slug: string = '/agents/visibility-intelligence/analysis';

        return this.neptuneApi.api<ViAnalysisResponseApi>(slug, request, HttpVerbsEnum.POST);
    }

    getRuns(id: number, queryParams: ViRunRequestApi): Observable<ViRunResponseApi[]> {
        const slug: string = `/agents/visibility-intelligence/monitor/${id}/runs`;

        return this.neptuneApi.api<any>(slug, null, HttpVerbsEnum.GET, queryParams);
    }
}
